import MicroModal from 'micromodal';
import { initializeAccommGallerySlideshow, goToAccommSlide } from './slideshow-manager.js';
import { getLenisInstance } from '../core/scroll-manager.js';

// --- Module-level state ---
let modalListenersInitialized = false;
let DEBUG_MODE = false;
let activeTriggerElement = null; // To store the element that opened the modal
let clickHandler = null;
let anchorHandler = null;

// --- Private Helper Functions ---

/**
 * Sets a cookie.
 * @param {string} name - The name of the cookie.
 * @param {string} value - The value of the cookie.
 * @param {number} days - The number of days until the cookie expires.
 */
function setCookie(name, value, days) {
    let expires = "";
    if (days) {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "") + expires + "; path=/";
}

/**
 * Gets a cookie.
 * @param {string} name - The name of the cookie.
 * @returns {string|null} The cookie value or null if not found.
 */
function getCookie(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
}

/**
 * Shows a modal with specific configuration derived from its data attributes.
 * @param {string} dataId - The value of the modal's `data-micromodal-id` to show.
 */
function showModal(dataId, triggerEl = null) {
    const modal = document.querySelector(`[data-micromodal-id="${dataId}"]`);
    if (!modal) {
        if (DEBUG_MODE) console.warn(`Modal with data-micromodal-id="${dataId}" not found.`);
        return;
    }

    // --- Special handling for the Accommodation Gallery Modal ---
    if (dataId === 'accomm-gallery-modal') {
        // Ensure the slideshow is initialized before showing the modal.
        initializeAccommGallerySlideshow(DEBUG_MODE);

        // If a trigger element was passed (the clicked image), get its index.
        if (triggerEl && triggerEl.dataset.imageIndex) {
            const slideIndex = parseInt(triggerEl.dataset.imageIndex, 10);
            goToAccommSlide(slideIndex);
        }
    }

    // Don't show if another modal is already open
    if (document.querySelector('.micromodal.is-open')) {
        return;
    }

    const expireDays = parseInt(modal.dataset.modalExpire, 10);
    const cookieName = `modal_seen_${modal.id}`; // Cookie name uses the unique element ID

    if (expireDays > 0 && getCookie(cookieName)) {
        return; // Don't show if cookie is set
    }

    const useHashTracking = modal.dataset.micromodalOptions?.includes('hashTracking: true') || false;

    MicroModal.show(modal.id);
}

/**
 * Destroys modal listeners and resets the initialization state.
 * This is crucial for Barba.js transitions to ensure a clean re-initialization.
 */
export function destroyModals() {
    // MicroModal doesn't have a built-in destroy method, so we manually
    // reset our state to allow re-initialization.
    // The event listeners added by .init() are on the document and will be
    // overwritten by the next .init() call.
    if (modalListenersInitialized) {
        modalListenersInitialized = false;
        activeTriggerElement = null;
        if (clickHandler) document.body.removeEventListener('click', clickHandler);
        if (anchorHandler) document.body.removeEventListener('click', anchorHandler, true);
        clickHandler = null;
        anchorHandler = null;
        if (DEBUG_MODE) console.log('Modals: State reset for re-initialization.');
    }
}

// --- Public Exported Function ---

/**
 * Initializes all modal functionality, including click triggers and auto-triggers.
 * Should be called on initial page load and after every Barba page transition.
 * @param {boolean} [debug=false] - Whether to enable debug logging.
 */
export function initializeModals(debug = false) {
    DEBUG_MODE = debug;

    // --- 1. Initialize MicroModal globally and set up delegated click listener (only once) ---
    if (!modalListenersInitialized) {
        try {
            MicroModal.init({
                awaitOpenAnimation: true,
                awaitCloseAnimation: true, // Ensures animations complete before closing.
                disableScroll: false, // Let Lenis handle scroll locking.
                disableFocus: false, // We manage focus manually.

                onShow: (modal, trigger) => {
                    getLenisInstance()?.stop();
                    activeTriggerElement = trigger; // Store the element that opened the modal.

                    // Manually handle hash tracking on show
                    const useHashTracking = modal.dataset.micromodalOptions?.includes('hashTracking: true') || false;
                    if (useHashTracking && window.location.hash !== `#${modal.id}`) {
                        history.pushState(null, null, `#${modal.id}`);
                    }
                },
                onClose: (modal) => {
                    getLenisInstance()?.start();

                    // Manually handle hash tracking on close
                    const useHashTracking = modal.dataset.micromodalOptions?.includes('hashTracking: true') || false;
                    if (useHashTracking && window.location.hash === `#${modal.id}`) {
                        history.pushState(null, null, window.location.pathname + window.location.search);
                    }

                    // Return focus to the original trigger element.
                    if (activeTriggerElement && activeTriggerElement.focus) {
                        activeTriggerElement.focus();
                    }
                    activeTriggerElement = null; // Clear the reference.
                }
            });

            window.MicroModal = MicroModal;
        } catch (e) {
            console.error("Could not initialize MicroModal", e);
            return;
        }

        clickHandler = (event) => {
            const trigger = event.target.closest('[data-micromodal-trigger]');
            if (trigger) {
                event.preventDefault();
                const dataId = trigger.dataset.micromodalTrigger;
                showModal(dataId, trigger); // Pass the trigger element
            }
        };
        document.body.addEventListener('click', clickHandler);

        // Listener for anchor links (e.g., href="#contact") that should open modals.
        // It runs in the "capture" phase to act before other listeners (like Lenis smooth scroll).
        anchorHandler = (event) => {
            const anchor = event.target.closest('a[href*="#"]');

            // If it's not an anchor link with a hash, or it's already a micromodal trigger, ignore it.
            if (!anchor || anchor.hasAttribute('data-micromodal-trigger')) {
                return;
            }

            // Check if the link is for the current page.
            const linkUrl = new URL(anchor.href, window.location.href);
            const currentUrl = new URL(window.location.href);

            if (linkUrl.origin !== currentUrl.origin || linkUrl.pathname !== currentUrl.pathname) {
                // It's a link to a different page, let Barba/browser handle it.
                return;
            }

            const hash = linkUrl.hash.substring(1);
            if (!hash) {
                return; // No hash, nothing to do.
            }

            // Check if a modal with this data-id exists.
            const modal = document.querySelector(`[data-micromodal-id="${hash}"]`);

            if (modal) {
                // A modal exists for this hash. Prevent default scroll and show the modal.
                event.preventDefault();
                event.stopPropagation(); // Prevent Lenis from handling this click.
                showModal(hash); // No trigger element here, will default to first slide
            }
            // If no modal is found, do nothing and let the default anchor behavior (or Lenis) proceed.
        };
        document.body.addEventListener('click', anchorHandler, true); // Use capture phase to ensure this runs before other click listeners.

        modalListenersInitialized = true;
    }

    // --- 2. Handle auto-triggered modals (exit-intent) ---
    // This part runs on every page load/transition.
    const autoModals = document.querySelectorAll('[data-modal-type="exit-intent"][data-modal-active="page-load"]');
    let exitIntentSetup = false;

    autoModals.forEach(modal => {
        if (exitIntentSetup) return; // Only setup one exit-intent listener per page.

        const delay = parseInt(modal.dataset.modalDelay, 10) || 0;
        const exitIntentHandler = (e) => {
            if (e.clientY < 20 && e.movementY < 0) {
                // Use the data-micromodal-id for showing the modal
                showModal(modal.dataset.micromodalId);
                document.removeEventListener('mouseout', exitIntentHandler);
            }
        };
        setTimeout(() => {
            document.addEventListener('mouseout', exitIntentHandler);
            exitIntentSetup = true;
        }, delay);
    });

    // --- 3. Handle hash-triggered modals on page load/transition ---
    const hash = window.location.hash.substring(1);
    if (hash) {
        const modalFromHash = document.querySelector(`[data-micromodal-id="${hash}"]`);

        // Check if the element exists, is a micromodal, and has hash tracking enabled.
        if (modalFromHash && modalFromHash.classList.contains('micromodal') && modalFromHash.dataset.micromodalOptions?.includes('hashTracking: true')) {
            // Use a small timeout to ensure the page is settled, especially after a Barba transition,
            // before showing the modal. This helps prevent race conditions.
            // before showing the modal. This helps prevent race conditions with other scripts.
            setTimeout(() => {
                showModal(hash); // `hash` is the data-micromodal-id
                // The hash matches the modal's actual ID, which MicroModal uses for hash tracking.
                MicroModal.show(hash, { hashTracking: true });
            }, 150);
        }
    }
}